import React, { useEffect, useRef, useState } from "react";
import Linkify from "react-linkify";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSpinner,
  IonText,
  IonTextarea,
} from "@ionic/react";
import { closeOutline, paperPlane } from "ionicons/icons";

import { useActivity } from "../data/store/useActivity";
import { useConversations } from "../data/store/useConversations";
import { useProfile } from "../data/store/useProfile";

import { ArtistId } from "../models/Artist";
import { FreshMessageNode, MessageNode } from "../models/Message";

import AsyncArtistButton from "./AsyncArtistButton";
import ConversationDeleteModal from "./ConversationDeleteModal";
import { usePageTitle } from "../util/sugar";
import "./styles.scss";

interface MessageWidgetProps {
  id: string;
}

const renderMessages = (
  messages: MessageNode[],
  currentAuthorId: ArtistId | undefined,
) => {
  return messages.map((message, index) => {
    if (message.text.length === 0) {
      return <React.Fragment key={index} />;
    }

    const isOwn = !!currentAuthorId && message.authorId === currentAuthorId;

    return (
      <IonItem
        class="ion-no-padding"
        className={`message-item ${isOwn ? "own" : "other"}`}
        key={message.text + index}
        lines="none"
        detail={false}
        color="light"
      >
        {!isOwn && (
          <div className="message-avatar">
            <AsyncArtistButton variant="avatar" id={message.authorId} />
          </div>
        )}

        <div className={`message-bubble ${isOwn ? "own" : "other"}`}>
          {!isOwn && (
            <div className="message-meta">
              <AsyncArtistButton
                id={message.authorId}
                key={index}
                variant="song-alias"
              />
              <IonText className="message-timestamp" color="medium" mode="ios">
                {message.timestamp &&
                  `${new Date(+message.timestamp).toLocaleTimeString([], {
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`}
              </IonText>
            </div>
          )}

          <IonLabel
            style={{ display: "inline" }}
            className="ion-text-wrap message-label"
          >
            <Linkify>{message.text}</Linkify>
          </IonLabel>

          {isOwn && (
            <div className="message-meta own">
              <IonText className="message-timestamp" color="medium" mode="ios">
                {message.timestamp &&
                  new Date(+message.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
              </IonText>
            </div>
          )}
        </div>
      </IonItem>
    );
  });
};

const someOneIsTypingMessage = (id: ArtistId) => (
  <IonItem key={"someone-is-typing"} lines="none" detail={false} color="light">
    <div className="message-avatar">
      <AsyncArtistButton variant="avatar" id={id} />
    </div>
    <div className="message-bubble other typing">
      <IonSpinner name="dots" />
    </div>
  </IonItem>
);

const TYPING_THROTTLE_MS = 4000;
const TYPING_STOP_MS = 3000;

const MessageWidget: React.FC<MessageWidgetProps> = ({ id }) => {
  const conversation = useConversations((x) => x.retrieve(id));
  const [removeConversationModal, setRemoveConversationModal] = useState(false);
  const { transmitActivity } = useActivity();
  const someOneIsTyping = useConversations((x) => x.someOneIsTyping);
  const { transmit, leave } = useConversations();
  const { authorId, darkMode, ownerId } = useProfile();
  const messages = (conversation && conversation.messages) || [];

  const [messagesPerPage, setMessagesPerPage] = useState(10);
  const [freshMessage, setMessage] = useState("");
  const conversationContentRef = useRef<HTMLIonContentElement>(null);
  const messageTextAreaRef = useRef<HTMLIonTextareaElement>(null);
  const lastTypingSentRef = useRef(0);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const messagesSlice = messages.slice(-messagesPerPage);
  const memberTypingId = authorId && someOneIsTyping(id, authorId);
  const convoTitle = conversation ? conversation.title : "loading";
  const convoLength = conversation ? conversation.members.length : 0;

  usePageTitle(convoTitle);

  const stopTypingIndicator = () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (authorId && lastTypingSentRef.current !== 0) {
      transmitActivity("typing", "conversations", id, authorId, false);
    }
    lastTypingSentRef.current = 0;
  };

  // Fire a debounced "typing" activity on every keystroke. Throttle the
  // "typing: true" signal so we don't spam the server, and schedule a
  // "typing: false" once the user pauses.
  const triggerTypingIndicator = () => {
    if (!authorId) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current > TYPING_THROTTLE_MS) {
      transmitActivity("typing", "conversations", id, authorId, true);
      lastTypingSentRef.current = now;
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      if (authorId) {
        transmitActivity("typing", "conversations", id, authorId, false);
      }
      lastTypingSentRef.current = 0;
      typingTimeoutRef.current = null;
    }, TYPING_STOP_MS);
  };

  const handleTransmitMessage = () => {
    if (!freshMessage || !conversation) return;
    const messageNode = {
      id,
      authorId: authorId,
      ownerId,
      text: freshMessage,
    } as FreshMessageNode;

    transmit(conversation.id, messageNode);
    setMessage("");
    stopTypingIndicator();
  };

  // Enter sends; Shift+Enter inserts a newline (default textarea behavior).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLIonTextareaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTransmitMessage();
    }
  };

  const handleBlur = () => {
    // Give the user a moment to come back before clearing — avoids flicker
    // when focus briefly leaves the textarea (e.g. clicking inside the chat).
    setTimeout(stopTypingIndicator, 1500);
  };

  // Scroll to bottom on new messages. Use rAF to wait for the DOM update,
  // then a short (250ms) scroll animation so it feels responsive.
  useEffect(() => {
    requestAnimationFrame(() => {
      conversationContentRef.current?.scrollToBottom(250);
    });
  }, [messages.length]);

  useEffect(() => {
    // Clean up any pending typing timeout on unmount / convo switch.
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [id]);

  return (
    <IonContent ref={conversationContentRef}>
      <IonItem lines="none" color="light">
        <IonButtons slot="end">
          <IonButton
            slot="end"
            className="ion-float-right"
            size="large"
            onClick={() => setRemoveConversationModal(true)}
            aria-label="Conversation options"
          >
            <IonIcon
              className="--ion-padding"
              size="large"
              color={"dark"}
              icon={closeOutline}
              aria-hidden="true"
            />
          </IonButton>

          {removeConversationModal && (
            <ConversationDeleteModal
              onComplete={() => setRemoveConversationModal(false)}
              id={id}
            />
          )}
        </IonButtons>
      </IonItem>

      <IonList style={{ background: darkMode ? "#333" : "#fff" }}>
        {messages.length >= messagesPerPage && (
          <IonButton
            color="primary"
            expand="full"
            onClick={() => setMessagesPerPage((prevState) => prevState + 10)}
          >
            Show Older
          </IonButton>
        )}

        {messages && renderMessages(messagesSlice, authorId)}

        {memberTypingId && someOneIsTypingMessage(memberTypingId)}

        {id !== "" && (
          <div
            className="message-compose-row"
            key={id + "-newMessage"}
          >
            {convoLength !== 1 && (
              <IonTextarea
                className="message-textarea"
                inputmode="text"
                autoGrow
                ref={messageTextAreaRef}
                onBlur={handleBlur}
                key={id + "-fresh-message"}
                aria-label="Message"
                placeholder="Type a message — Enter to send, Shift+Enter for new line"
                onKeyDown={handleKeyDown}
                onIonChange={(e) => {
                  setMessage(e.detail.value!);
                  triggerTypingIndicator();
                }}
                value={freshMessage}
              />
            )}

            <IonButton
              disabled={!(freshMessage && authorId)}
              onClick={() => {
                handleTransmitMessage();
              }}
              className="message-send-button"
              fill="clear"
              aria-label="Send message"
            >
              <IonIcon size="large" icon={paperPlane} aria-hidden="true" />
            </IonButton>

            {convoLength === 1 && (
              <IonButton
                routerLink={"/conversations?thread=start"}
                onClick={() => {
                  conversation &&
                    ownerId &&
                    authorId &&
                    leave(ownerId, authorId, conversation.id);
                }}
              >
                Leave conversation
              </IonButton>
            )}
          </div>
        )}

        <IonItem lines="none" color="clear" />
        <IonItem lines="none" color="clear" />
        <IonItem lines="none" color="clear" />
      </IonList>
    </IonContent>
  );
};

export default MessageWidget;
